/**
 * 「家长批改」的**纯逻辑**（可测，不碰网络、不碰 DOM）。
 *
 * ## 这一层存在的唯一理由：**只点错的**
 *
 * 家长批改的真实动作是"扫一遍，只点错的那几题"。这件事在协议层的落点是
 * "**没给到的题按 `correct` 补齐**"，而那个默认值必须在界面上是**显式**的、
 * 并且在提交时**绝不会漏题**。这两条都是容易写错、又很难在生产上发现的地方：
 *
 *   · 漏了一题 → 那道题永远停在"还没批改"，会话永远到不了"全部批改完"；
 *   · 默认值没说清 → 家长以为"没点的是没批改"，于是把所有题都点一遍
 *     （那就完全违背了"只点错的"这件事）。
 *
 * 所以"初值 / 改成什么 / 提交什么"三件事全部收在这里的纯函数里，有单测。
 */
import type { DictationMarkView, DictationQuestionView } from './api/dictationSessions';

/** 三个判定的顺序（界面上按钮的固定顺序：对 → 错 → 没写出来）。 */
export const MARK_ORDER: readonly DictationMarkView[] = ['correct', 'wrong', 'missing'];

/**
 * 界面上每个判定的文案。
 *
 * ⚠️ `wrong`（写错了）与 `missing`（**没写出来**）必须用不同的话：
 *   用户原话是"没有听写出来**或者**错的词"——这是两种不同的状态，
 *   家长的辅导方式也不同（一个是纠错别字，一个是从头教）。
 */
export const MARK_LABEL: Record<DictationMarkView, string> = {
  correct: '对',
  wrong: '写错了',
  missing: '没写出来',
};

/** 判定的完整说法（选择器上的标题 / 无障碍标签）。 */
export const MARK_FULL_LABEL: Record<DictationMarkView, string> = {
  correct: '写对了',
  wrong: '写了，但写错了',
  missing: '没写出来（空着 / 不会写）',
};

/** 认不出来的判定原样显示，不静默成空。 */
export function markLabel(mark: DictationMarkView | null | undefined): string {
  if (mark === null) return '还没批改';
  if (mark === undefined) return '—';
  return MARK_LABEL[mark] ?? mark;
}

/**
 * 这次会话的**初始判定表**（题 id → 判定）。
 *
 * 初值规则：
 *   · 已经有判定的题（会话是 `graded`，家长回来改）→ **沿用库里的判定**；
 *   · 还没有判定的题（第一次批改）→ `correct`（**默认全对**）。
 *
 * ⚠️ 为什么"已经有判定的题"不也一律给 `correct`：那会让家长点开一个已批改的
 *   会话时看到**全是对**（他上次标的那几个错全没了），一提交就把错词本改坏。
 */
export function initialMarks(
  questions: readonly DictationQuestionView[],
): Record<string, DictationMarkView> {
  const marks: Record<string, DictationMarkView> = {};
  for (const question of questions) {
    marks[question.id] =
      question.mark === 'correct' || question.mark === 'wrong' || question.mark === 'missing'
        ? question.mark
        : 'correct';
  }
  return marks;
}

/**
 * 判定表 → 提交用的数组（**每道题都要在里面**）。
 *
 * ⚠️ 这是"默认全对"能成立的**前提**：后端只会把没给到的题当 `correct`，
 *   而这里显式给全 —— 两者一致，所以"没点的题算对"这件事不会因为
 *   前端漏发而变成"没批改"。
 */
export function buildGradeMarks(
  questions: readonly DictationQuestionView[],
  marks: Readonly<Record<string, DictationMarkView>>,
): { questionId: string; mark: DictationMarkView }[] {
  return questions.map((question) => ({
    questionId: question.id,
    mark: marks[question.id] ?? 'correct',
  }));
}

/** 这批题里有多少道是"不是全对"的（家长最关心的那个数）。 */
export function countProblems(marks: Readonly<Record<string, DictationMarkView>>): {
  wrong: number;
  missing: number;
  total: number;
} {
  let wrong = 0;
  let missing = 0;
  let total = 0;
  for (const mark of Object.values(marks)) {
    total += 1;
    if (mark === 'wrong') wrong += 1;
    if (mark === 'missing') missing += 1;
  }
  return { wrong, missing, total };
}

/**
 * 一道题的"历史错误次数" → 人话。
 *
 * ⚠️ 拿不到（`mistake === undefined`）时说「—」而**不是**"没错过"：
 *   "这个词从没错过"与"这个词的错词记录没拿到"在辅导决策上是完全不同的两件事
 *   （前者可以放心，后者应该先刷新看一眼）。
 */
export function describeQuestionHistory(question: DictationQuestionView): string {
  if (question.mistake === undefined) return '历史错误次数未获取';
  if (question.mistake === null) return '第一次听写这个词';
  const { wrongCount, missingCount } = question.mistake;
  const parts: string[] = [];
  if (wrongCount > 0) parts.push(`写错 ${wrongCount} 次`);
  if (missingCount > 0) parts.push(`没写出来 ${missingCount} 次`);
  if (parts.length === 0) return '第一次听写这个词';
  return `这个词以前${parts.join('、')}`;
}

/** 批改结果（后端返回的计数）→ 提交后给家长看的一句话。 */
export function describeGradeOutcome(result: {
  changedQuestions: number;
  unchangedQuestions: number;
  newMistakes: number;
  resolvedMistakes: number;
  /** 因为"判定改成对"而整条撤掉的错词记录（可选：老后端没有这个字段）。 */
  removedMistakes?: number;
  touchedMistakes: number;
  openMistakeCount: number;
}): string {
  if (result.changedQuestions === 0) {
    return `没有改动（${result.unchangedQuestions} 道题的判定和上次一样），错词本也没有变化。`;
  }
  const parts = [`改了 ${result.changedQuestions} 道题的判定`];
  if (result.newMistakes > 0) parts.push(`新登记 ${result.newMistakes} 个错词`);
  if (result.resolvedMistakes > 0) parts.push(`${result.resolvedMistakes} 个词已经掌握`);
  /**
   * "撤销"是 2026-10-29 新增的口径：家长把某题从"错"改成"对"时，那条错词记录
   * 会被**整条撤掉**（不是留在错词本里记 0 次）。不写出来，家长会以为"改了却没生效"。
   */
  const removed = result.removedMistakes ?? 0;
  if (removed > 0) parts.push(`撤销了 ${removed} 条错词记录（那些题改成「对」了）`);
  parts.push(`错词本里还有 ${result.openMistakeCount} 个词没掌握`);
  return `${parts.join('，')}。`;
}
